import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { FC, useMemo, useState } from "react";
import { ActionModerationStatus, ModerationAction } from "src/apis/action";
import NoDataAlert from "src/pages/components/NoDataAlert";
import { formatDateToBR } from "src/utils/date";

const ROWS = 10;

const STATUS_META: Record<
  ActionModerationStatus,
  { label: string; color: "warning" | "success" | "error" }
> = {
  pending: { label: "Pendente", color: "warning" },
  accepted: { label: "Aceita", color: "success" },
  rejected: { label: "Rejeitada", color: "error" },
};

type Props = {
  actions: ModerationAction[];
  onAccept: (item: ModerationAction) => void;
  onReject: (item: ModerationAction) => void;
};

const ActionsTable: FC<Props> = ({ actions, onAccept, onReject }): JSX.Element => {
  const hasData = useMemo(() => Boolean(actions) && actions.length > 0, [actions]);
  const [page, setPage] = useState(0);

  if (!hasData) return <NoDataAlert />;

  const pageSlice = actions.slice(page * ROWS, page * ROWS + ROWS);

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight={600}>Data</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Psicólogo</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Título</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Ação</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Empresa</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Participantes</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Status</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={600}>Opções</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageSlice.map((item) => {
              const meta = STATUS_META[item.status];
              return (
                <TableRow key={item.id} hover>
                  <TableCell>{formatDateToBR(item.data)}</TableCell>
                  <TableCell>{item.psicologo ?? "-"}</TableCell>
                  <TableCell>{item.titulo ?? "-"}</TableCell>
                  <TableCell>{item.acao ?? "-"}</TableCell>
                  <TableCell>{item.empresa ?? "-"}</TableCell>
                  <TableCell>{item.numero_participantes ?? "-"}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        item.status === "rejected" && item.rejection_reason
                          ? `Motivo: ${item.rejection_reason}`
                          : ""
                      }
                    >
                      <Chip size="small" label={meta.label} color={meta.color} />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {item.status !== "accepted" && (
                        <Tooltip title="Aceitar">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => onAccept(item)}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {item.status !== "rejected" && (
                        <Tooltip title="Rejeitar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onReject(item)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={actions.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={ROWS}
        rowsPerPageOptions={[ROWS]}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </>
  );
};

export default ActionsTable;
